import Link from 'next/link'
import { RezaroLogo } from './rezaro-logo'

type Lang = 'de' | 'en' | 'tr'

const FOOTER_HREFS = {
  'Funktionen': [
    '/reservierungen',
    '/reservierungen/digitales-reservierungsbuch',
    '/reservierungs-tool',
    '/bestellungen',
    '/integrationen',
  ],
  'Planung': [
    '/planung',
    '/planung/tischplan',
    '/planung/anzahlung',
  ],
  'Branchen': [
    '/branchen/italienisch',
    '/branchen/sushi',
    '/branchen/tuerkisch',
    '/branchen/burger-streetfood',
    '/branchen',
  ],
  'Standorte': [
    '/reservierungssystem/berlin',
    '/reservierungssystem/hamburg',
    '/reservierungssystem/muenchen',
    '/reservierungssystem/frankfurt',
    '/reservierungssystem',
  ],
  'Unternehmen': [
    '/pricing',
    '/use-cases',
    '/blog',
    '/service',
    '/hilfe',
    '/faq',
    '/status',
  ],
} as const

const LEGAL_HREFS = [
  '/agb',
  '/datenschutz',
  '/widerrufsbelehrung',
  '/impressum',
  '/bildquellen',
  '/barrierefreiheit',
]

type ColumnKey = keyof typeof FOOTER_HREFS

interface FooterStrings {
  columns: Record<ColumnKey, { heading: string; labels: string[] }>
  legal: string[]
  hours: string
  copyright: string
  hosting: string
}

const FOOT_T: Record<Lang, FooterStrings> = {
  de: {
    columns: {
      'Funktionen': {
        heading: 'Funktionen',
        labels: [
          'Reservierungen',
          'Digitales Reservierungsbuch',
          'Reservierungs-Widget',
          'Bestellungen',
          'Integrationen',
        ],
      },
      'Planung': {
        heading: 'Planung',
        labels: ['Übersicht', 'Tischplan', 'Anzahlungen'],
      },
      'Branchen': {
        heading: 'Branchen',
        labels: ['Italienisch', 'Sushi', 'Türkisch', 'Burger & Streetfood', 'Alle Branchen'],
      },
      'Standorte': {
        heading: 'Standorte',
        labels: ['Berlin', 'Hamburg', 'München', 'Frankfurt', 'Alle Städte'],
      },
      'Unternehmen': {
        heading: 'Unternehmen',
        labels: ['Preise', 'Use Cases', 'Blog', 'Service', 'Hilfe-Center', 'FAQ', 'Status'],
      },
    },
    legal: ['AGB', 'Datenschutz', 'Widerruf', 'Impressum', 'Bildquellen', 'Barrierefreiheit'],
    hours: 'Mo – Fr, 09–18 Uhr',
    copyright: '© 2026 Rezaro ·',
    hosting: '🇩🇪 Server in Deutschland · DSGVO-konform',
  },
  en: {
    columns: {
      'Funktionen': {
        heading: 'Features',
        labels: [
          'Reservations',
          'Digital Reservation Book',
          'Reservation Widget',
          'Orders',
          'Integrations',
        ],
      },
      'Planung': {
        heading: 'Planning',
        labels: ['Overview', 'Table Plan', 'Deposits'],
      },
      'Branchen': {
        heading: 'Industries',
        labels: ['Italian', 'Sushi', 'Turkish', 'Burgers & Street Food', 'All Industries'],
      },
      'Standorte': {
        heading: 'Locations',
        labels: ['Berlin', 'Hamburg', 'Munich', 'Frankfurt', 'All Cities'],
      },
      'Unternehmen': {
        heading: 'Company',
        labels: ['Pricing', 'Use Cases', 'Blog', 'Service', 'Help Center', 'FAQ', 'Status'],
      },
    },
    legal: ['Terms', 'Privacy', 'Right of Withdrawal', 'Imprint', 'Image Credits', 'Accessibility'],
    hours: 'Mon – Fri, 9 am – 6 pm',
    copyright: '© 2026 Rezaro ·',
    hosting: '🇩🇪 Servers in Germany · GDPR-compliant',
  },
  tr: {
    columns: {
      'Funktionen': {
        heading: 'Özellikler',
        labels: [
          'Rezervasyonlar',
          'Dijital Rezervasyon Defteri',
          'Rezervasyon Widget’ı',
          'Siparişler',
          'Entegrasyonlar',
        ],
      },
      'Planung': {
        heading: 'Planlama',
        labels: ['Genel Bakış', 'Masa Planı', 'Ön Ödemeler'],
      },
      'Branchen': {
        heading: 'Sektörler',
        labels: ['İtalyan', 'Suşi', 'Türk', 'Burger & Sokak Lezzetleri', 'Tüm Sektörler'],
      },
      'Standorte': {
        heading: 'Lokasyonlar',
        labels: ['Berlin', 'Hamburg', 'Münih', 'Frankfurt', 'Tüm Şehirler'],
      },
      'Unternehmen': {
        heading: 'Şirket',
        labels: ['Fiyatlar', 'Kullanım Senaryoları', 'Blog', 'Servis', 'Yardım Merkezi', 'SSS', 'Durum'],
      },
    },
    legal: ['Şartlar', 'Gizlilik', 'Cayma Hakkı', 'Künye', 'Görsel Kaynakları', 'Erişilebilirlik'],
    hours: 'Pzt – Cum, 09–18',
    copyright: '© 2026 Rezaro ·',
    hosting: '🇩🇪 Sunucular Almanya’da · GDPR uyumlu',
  },
}

export function PublicFooter({ lang = 'de' }: { lang?: Lang }) {
  const t = FOOT_T[lang]
  const columnKeys = Object.keys(FOOTER_HREFS) as ColumnKey[]

  return (
    <footer className="border-t border-pub-rule bg-pub-dark" style={{ fontFamily: 'var(--font-pub)' }}>
      <div className="mx-auto max-w-7xl px-6 py-16">
        <div className="grid grid-cols-2 gap-10 md:[grid-template-columns:minmax(0,200px)_repeat(6,minmax(0,1fr))]">

          <div className="col-span-2 md:col-auto">
            <div className="mb-5">
              <RezaroLogo variant="dark" size="md" showWordmark showTagline={false} />
            </div>
            <address className="not-italic text-[13px] text-white/60 leading-relaxed mb-4">
              CEYMEDIA — Rezaro<br />
              Bahnhofsplatz 2<br />
              70806 Kornwestheim
            </address>
            <p className="text-[12px] font-medium text-white/60 mb-1">{t.hours}</p>
            <a href="mailto:info@rezaro.de"
              className="text-[13px] text-pub-accent hover:text-white transition-colors">
              info@rezaro.de
            </a>
          </div>

          {columnKeys.map((key) => {
            const hrefs = FOOTER_HREFS[key]
            const col = t.columns[key]
            return (
              <div key={key}>
                <p className="mb-4 text-[11px] font-semibold uppercase tracking-widest text-white/60">{col.heading}</p>
                <ul className="space-y-2.5">
                  {hrefs.map((href, i) => (
                    <li key={href}>
                      <Link href={href} className="text-[13px] text-white/70 hover:text-white transition-colors">
                        {col.labels[i]}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 md:flex-row">
          <p className="text-[12px] text-white/60">
            {t.copyright}{' '}
            {LEGAL_HREFS.map((href, i) => (
              <span key={href}>
                {i > 0 && ' · '}
                <Link href={href} className="hover:text-white transition-colors">{t.legal[i]}</Link>
              </span>
            ))}
          </p>
          <div className="flex items-center gap-5">
            <span className="text-[12px] text-white/60">{t.hosting}</span>
          </div>
        </div>
      </div>
    </footer>
  )
}
